/**
 * On mount: parse iframe's URL, expand event via registry, execute iframe-kind
 * commands locally. Parent-kind commands (e.g. widget.scrollIntoView) are
 * handled by the parent widget script, which reads the same URL independently.
 *
 * Backward compat: ?property=ID translates to event property.open with id=ID.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWidgetEvent } from '../contexts/WidgetEventContext';
import { DEEP_LINK_PARAM, EVENT_PARAM, PARAM_PREFIX } from '../config/deepLink';
import { expandEvent } from '../widget/event-registry';
import { executeIframeCommand } from '../widget/command-handlers-iframe';
import type { EventParams, IframeCommand } from '../widget/types';

interface UrlEvent {
  name: string;
  params: EventParams;
}

const readUrlEvent = (search: string): UrlEvent | null => {
  const params = new URLSearchParams(search);

  const eventName = params.get(EVENT_PARAM);
  if (eventName) {
    const eventParams: EventParams = {};
    params.forEach((value, key) => {
      if (key === EVENT_PARAM) return;
      if (!key.startsWith(PARAM_PREFIX)) return;
      eventParams[key.slice(PARAM_PREFIX.length)] = value;
    });
    return { name: eventName, params: eventParams };
  }

  const legacyId = params.get(DEEP_LINK_PARAM);
  if (legacyId) {
    return { name: 'property.open', params: { id: legacyId } };
  }

  return null;
};

export const useUrlEventDispatch = (): void => {
  const navigate = useNavigate();
  const { setLendiPending } = useWidgetEvent();

  useEffect(() => {
    const urlEvent = readUrlEvent(window.location.search);
    if (!urlEvent) return;

    let commands;
    try {
      commands = expandEvent(urlEvent.name, urlEvent.params);
    } catch (e) {
      console.warn('[TwoPoint iframe] URL event rejected:', (e as Error).message);
      return;
    }

    commands.forEach((cmd) => {
      if (cmd.kind !== 'iframe') return;
      executeIframeCommand(cmd, { navigate, setLendiPending });
    });
  }, [navigate, setLendiPending]);
};
